import type { ConnectorFolder, KnowledgeBase } from '@ralph/contracts'
import { SURFACE_LABEL, SYNC_INTERVALS } from '@ralph/contracts'
import { Clock, ExternalLink, Info, KeyRound, PauseCircle, Play, RefreshCw, TriangleAlert, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useFocusTrap } from '@/components/ui/focus-trap.ts'
import { Overlay } from '@/components/ui/Overlay.tsx'
import { Button, ChoiceRow, IconButton, Readout, Spinner } from '@/components/ui/primitives.tsx'
import {
  useCheckNow,
  useConnections,
  useConnectorAvailability,
  useDisconnectFolder,
  useFolder,
  useItems,
  useLeftOut,
  usePauseFolder,
  usePutBack,
  useResumeFolder,
  useRetryFailed,
  useStartFolder,
  useStartSignIn,
  useSyncs,
  useUpdateFolder,
  useViewer
} from '@/hooks/use-connectors.ts'
import { useBaseAccess } from '@/hooks/use-knowledge-bases.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'
import {
  canRenew,
  dueIn,
  folderAckText,
  folderReaders,
  mayUseConnection,
  needsPerson,
  ownerLeft,
  problemSentence,
  standingLabel,
  standingOf,
  syncLine
} from './connected-folders.ts'
import { FolderPreview, type PendingState } from './FolderPreview.tsx'
import { ProviderMark } from './ProviderMark.tsx'
import type { PendingRule } from './SelectionTable.tsx'

/**
 * One connected folder, opened up (PRD-25 §3).
 *
 * The standing block with its remedy first, because it is the one thing somebody has to act
 * on. Then the overview readouts, **What is included** (the same instrument as the preview,
 * editable, with the pending-changes bar), **Left out by you** with *Put back*, **Not loaded**
 * grouped by reason with a retry only where retrying can change the answer, **History** with
 * "nothing had changed" printed as a result, and a footer with the controls that decide what
 * happens next: Check now, Pause, how often, a different account, and Disconnect — which
 * offers *keep* and defaults to it.
 *
 * Everything that reads through the folder's account is offered only to someone who may use that
 * account — its owner, or an administrator (PRD-26 D-38); any other maintainer is told whose it
 * is, and keeps Pause and Disconnect, which only ever read less.
 */
type DrawerBase = Pick<KnowledgeBase, 'id' | 'name' | 'access' | 'owner' | 'lentToCount'>

export const FolderDrawer = ({
  folder: initial,
  base,
  canManage,
  onClose
}: {
  folder: ConnectorFolder
  base: DrawerBase
  canManage: boolean
  onClose: () => void
}) => {
  const live = useFolder(initial.id)
  const folder = live.data ?? initial
  const viewer = useViewer()
  // Bound inside the body, never at module scope (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  // May this viewer read through the folder's account? Everything that does is theirs to offer.
  const mayUse = canManage && mayUseConnection(folder.connection, viewer)
  const panelRef = useRef<HTMLDivElement>(null)
  const promptRef = useRef<HTMLDivElement>(null)
  const [pendingRules, setPendingRules] = useState<Map<string, PendingRule>>(new Map())
  // What the instrument holds unsaved — rules AND filters — with the server's count of the
  // consequence and the one call that applies it.
  const [pending, setPending] = useState<PendingState | null>(null)
  const [leaving, setLeaving] = useState(false)
  const leavingRef = useRef(false)
  leavingRef.current = leaving
  const dirty = pending !== null

  const requestClose = () => {
    if (dirty) setLeaving(true)
    else onClose()
  }
  // Escape closes the prompt it opened before it closes the drawer.
  useFocusTrap(panelRef, { onEscape: () => (leavingRef.current ? setLeaving(false) : requestClose()) })
  useEffect(() => {
    if (leaving) promptRef.current?.focus()
  }, [leaving])

  const standing = standingOf(folder)
  const problem = problemSentence(fmt, folder)
  const surface = SURFACE_LABEL[folder.surface]

  return (
    <Overlay>
      <div
        className="fade-in fixed inset-0 z-50 flex justify-end"
        role="dialog"
        aria-modal="true"
        aria-label={t.detail.drawerLabel(folder.displayPath)}
      >
        <button
          type="button"
          aria-label={t.detail.close}
          onClick={requestClose}
          className="flex-1 bg-[var(--scrim)]/25 backdrop-blur-[1px]"
        />
        <div
          ref={panelRef}
          tabIndex={-1}
          className="m-3 flex w-[720px] max-w-[94vw] flex-col rounded-panel border border-rule bg-card-solid shadow-frame focus:outline-none"
        >
          <header className="flex items-start justify-between gap-3 border-b border-rule px-4 py-3.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <ProviderMark surface={folder.surface} size={14} className="shrink-0 text-grounded" />
                <h2 className="truncate text-[15px]">{folder.displayPath}</h2>
              </div>
              <p className="readout mt-1 truncate text-fg-subtle">
                {surface} · {folder.connection.accountLabel} · {standingLabel(fmt, folder, standing)}
              </p>
            </div>
            <IconButton label={t.detail.close} size={29} onClick={requestClose}>
              <X size={14} aria-hidden />
            </IconButton>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {problem && (
              <div
                className={`border-b border-rule px-4 py-3 ${needsPerson(standing) ? 'bg-alert-soft' : 'bg-card-solid/55'}`}
              >
                <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-fg-muted">
                  {standing === 'needs_sign_in' ? (
                    <KeyRound size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
                  ) : standing === 'slowed' ? (
                    <Clock size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-fg-muted" aria-hidden />
                  ) : standing === 'paused' ? (
                    <PauseCircle size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-fg-muted" aria-hidden />
                  ) : (
                    <TriangleAlert size={13} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
                  )}
                  <span>
                    <span className="block font-semibold text-fg">{problem.title}</span>
                    {problem.body}
                  </span>
                </p>
                {canManage && <Remedies folder={folder} standing={standing} mayUse={mayUse} />}
              </div>
            )}

            {canManage && !mayUse && (
              <p className="flex items-start gap-2 border-b border-rule px-4 py-3 text-[12.5px] leading-relaxed text-fg-muted">
                <Info size={13} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
                <span>
                  {folder.connection.mode === 'delegated'
                    ? t.accounts.readsThroughOwner(folder.connection.ownerName)
                    : t.accounts.readsThroughCompany}
                </span>
              </p>
            )}

            <Overview folder={folder} base={base} />

            {folder.status !== 'previewing' && (
              <section className="border-b border-rule px-4 py-3.5" aria-labelledby="included-title">
                <h3 id="included-title" className="sr-only">
                  {t.selection.title}
                </h3>
                <FolderPreview
                  folder={folder}
                  base={base}
                  mode="edit"
                  readOnly={!mayUse}
                  pendingRules={pendingRules}
                  setPendingRules={setPendingRules}
                  onApplied={() => setLeaving(false)}
                  onPending={setPending}
                />
              </section>
            )}

            <LeftOut folder={folder} canChange={mayUse} />
            <NotLoaded folder={folder} canChange={mayUse} />
            <History folder={folder} />
          </div>

          {canManage && <Footer folder={folder} base={base} mayUse={mayUse} onDisconnected={onClose} />}

          {leaving && pending && (
            <div
              ref={promptRef}
              tabIndex={-1}
              role="alertdialog"
              aria-label={t.selection.pending.region}
              className="border-t border-rule bg-alert-soft px-4 py-3 focus:outline-none"
            >
              <p className="text-[12.5px] leading-[1.45] text-fg">
                {t.selection.pending.unsaved(fmt, pending.added ?? 0, pending.withdrawn ?? 0)}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={pending.withdrawn ? 'danger' : 'primary'}
                  disabled={pending.added === null}
                  onClick={async () => {
                    if (await pending.apply()) onClose()
                  }}
                >
                  {t.selection.pending.applyAndClose}
                </Button>
                <Button size="sm" variant="ghost" onClick={onClose}>
                  {t.selection.pending.discardAndClose}
                </Button>
                <Button size="sm" onClick={() => setLeaving(false)}>
                  {t.selection.pending.keepEditing}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Overlay>
  )
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

const Remedies = ({
  folder,
  standing,
  mayUse
}: {
  folder: ConnectorFolder
  standing: ReturnType<typeof standingOf>
  mayUse: boolean
}) => {
  const messages = useI18n().t
  const t = messages.knowledgeFolders
  const signIn = useStartSignIn()
  const check = useCheckNow()
  const resume = useResumeFolder()
  const viewer = useViewer()
  const mayRenew = canRenew(folder.connection, viewer)
  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-2">
      {standing === 'needs_sign_in' && !mayRenew && folder.connection.mode === 'delegated' && (
        <span className="text-[12.5px] text-fg-muted">{t.standing.ownerMustSignIn(folder.connection.ownerName)}</span>
      )}
      {/* Moving a folder off a departed person's sign-in reads through their account, which only
          an administrator may (D-38): an administrator signs in with their own and moves it. */}
      {ownerLeft(folder) && viewer.admin && folder.connection.provider !== 's3' && (
        <Button
          size="sm"
          variant="primary"
          icon={<KeyRound size={12} aria-hidden />}
          disabled={signIn.isPending}
          onClick={() =>
            signIn.mutate(
              { provider: folder.connection.provider as 'microsoft' | 'google', sourceId: folder.sourceId },
              { onSuccess: (started) => window.location.assign(started.authorizeUrl) }
            )
          }
        >
          {t.standing.signInWithMine}
        </Button>
      )}
      {standing === 'needs_sign_in' && mayRenew && folder.connection.provider !== 's3' && (
        <Button
          size="sm"
          variant="primary"
          icon={<KeyRound size={12} aria-hidden />}
          disabled={signIn.isPending}
          onClick={() =>
            signIn.mutate(
              {
                provider: folder.connection.provider as 'microsoft' | 'google',
                sourceId: folder.sourceId,
                reconnectConnectionId: folder.connection.id
              },
              { onSuccess: (started) => window.location.assign(started.authorizeUrl) }
            )
          }
        >
          {t.accounts.signInAgain}
        </Button>
      )}
      {mayUse && (standing === 'stopped' || standing === 'failed') && (
        <Button
          size="sm"
          variant="primary"
          icon={<RefreshCw size={12} aria-hidden />}
          disabled={check.isPending || resume.isPending}
          onClick={() =>
            folder.status === 'error' ? resume.mutate(folder.id) : check.mutate({ id: folder.id, walk: true })
          }
        >
          {t.standing.checkAgain}
        </Button>
      )}
      {(check.isError || resume.isError) && (
        <span role="alert" className="readout text-alert">
          {describeApiError(messages, check.error ?? resume.error)}
        </span>
      )}
    </div>
  )
}

const Overview = ({ folder, base }: { folder: ConnectorFolder; base: DrawerBase }) => {
  const viewer = useViewer()
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  const access = useBaseAccess(base.id, base.access.kind === 'groups')
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1.5 border-b border-rule px-4 py-3">
      <Readout
        label={t.detail.where}
        value={
          folder.webUrl ? (
            <a
              href={folder.webUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 underline decoration-transparent underline-offset-2 hover:decoration-current"
            >
              {folder.displayPath} <ExternalLink size={10} aria-hidden />
              <span className="sr-only">{t.document.opensNewTab(SURFACE_LABEL[folder.surface])}</span>
            </a>
          ) : (
            folder.displayPath
          )
        }
      />
      <Readout label={t.detail.account} value={folder.connection.accountLabel} />
      <Readout
        label={t.detail.readBy}
        value={t.access.readBy(fmt, folderReaders(fmt, base, viewer.id, access.data?.reach ?? null))}
      />
      <Readout
        label={t.detail.loaded}
        value={`${t.detail.loadedOf(fmt, folder.stats.synced, folder.stats.included)} · ${fmt.formatBytes(folder.stats.includedBytes)}`}
      />
      <Readout
        label={t.detail.lastChecked}
        value={folder.lastSuccessAt ? fmt.formatAgo(folder.lastSuccessAt) : fmt.t.common.never}
      />
      {folder.nextSyncAt && folder.status === 'active' && (
        <Readout label={t.detail.nextCheck} value={dueIn(fmt, folder.nextSyncAt)} />
      )}
    </div>
  )
}

const LeftOut = ({ folder, canChange }: { folder: ConnectorFolder; canChange: boolean }) => {
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  const leftOut = useLeftOut(folder.id)
  const putBack = usePutBack()
  const items = leftOut.data?.items ?? []
  if (folder.status === 'previewing') return null
  return (
    <section className="border-b border-rule px-4 py-3.5" aria-labelledby="left-out-title">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h3 id="left-out-title" className="font-sans text-[13.5px] font-semibold tracking-[-0.018em]">
          {t.detail.leftOutByYou}
        </h3>
        <span className="readout text-fg-subtle">{items.length === 0 ? '' : t.selection.files(fmt, items.length)}</span>
      </div>
      <p className="mt-0.5 text-[12.5px] leading-[1.45] text-fg-muted">
        {items.length === 0 ? t.detail.nothingLeftOut : t.detail.leftOutByYouBody}
      </p>
      {folder.orphanRules > 0 && (
        <p className="mt-1 text-[12px] text-fg-subtle">{t.detail.orphanRules(fmt, folder.orphanRules)}</p>
      )}
      {items.length > 0 && (
        <ul className="mt-2 space-y-1">
          {items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
              <span className="min-w-0 flex-1 truncate">
                {item.name}
                <span className="readout ml-2 text-fg-subtle">
                  {item.kind === 'folder'
                    ? t.detail.leftOutFolderKind(fmt, item.subtree.files)
                    : item.path.includes('/')
                      ? item.path.slice(0, item.path.lastIndexOf('/'))
                      : ''}
                </span>
              </span>
              {canChange && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={putBack.isPending}
                  onClick={() => putBack.mutate({ id: folder.id, remoteId: item.remoteId })}
                >
                  {t.detail.putBack}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

const NotLoaded = ({ folder, canChange }: { folder: ConnectorFolder; canChange: boolean }) => {
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  const failed = useItems(folder.id, { parent: null, filter: 'failed', query: '' })
  const availability = useConnectorAvailability()
  const retry = useRetryFailed()
  const [showFailed, setShowFailed] = useState(false)
  const stats = folder.stats
  if (folder.status === 'previewing' || (stats.unsupported === 0 && stats.tooLarge === 0 && stats.failed === 0))
    return null
  return (
    <section className="border-b border-rule px-4 py-3.5" aria-labelledby="not-loaded-title">
      <h3 id="not-loaded-title" className="font-sans text-[13.5px] font-semibold tracking-[-0.018em]">
        {t.detail.problems}
      </h3>
      <ul className="mt-2 space-y-2 text-[12.5px] leading-[1.45]">
        {stats.tooLarge > 0 && (
          <li>
            <span className="font-medium">{t.preview.tooLargeHeading(fmt, stats.tooLarge)}.</span>{' '}
            <span className="text-fg-muted">
              {t.preview.tooLargeBody(fmt.formatBytes(availability.data?.uploadMaxBytes ?? 25 * 1024 * 1024), null)}
            </span>
          </li>
        )}
        {stats.unsupported > 0 && (
          <li>
            <span className="font-medium">{t.preview.unsupportedHeading(fmt, stats.unsupported)}.</span>{' '}
            <span className="text-fg-muted">{t.preview.unsupportedBody}</span>
          </li>
        )}
        {stats.failed > 0 && (
          <li>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{t.detail.failedHeading(fmt, stats.failed)}.</span>
              {canChange && (
                <Button
                  size="sm"
                  icon={<RefreshCw size={12} aria-hidden />}
                  disabled={retry.isPending}
                  onClick={() => retry.mutate(folder.id)}
                >
                  {t.detail.retry}
                </Button>
              )}
              <Button size="sm" variant="ghost" aria-expanded={showFailed} onClick={() => setShowFailed(!showFailed)}>
                {showFailed ? t.preview.hideThem : t.preview.listThem}
              </Button>
            </div>
            <p className="text-fg-muted">{t.detail.retryHint(SURFACE_LABEL[folder.surface])}</p>
            {showFailed && (
              <ul className="mt-1 space-y-0.5">
                {(failed.data?.items ?? []).map((item) => (
                  <li key={item.id} className="flex flex-wrap gap-x-2">
                    <span>{item.path || item.name}</span>
                    <span className="readout text-alert">{item.error}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        )}
      </ul>
      <p className="mt-2 text-[12px] leading-[1.45] text-fg-subtle">{t.detail.twoCopies}</p>
    </section>
  )
}

const History = ({ folder }: { folder: ConnectorFolder }) => {
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  const syncs = useSyncs(folder.id)
  const items = syncs.data?.items ?? []
  return (
    <section className="px-4 py-3.5" aria-labelledby="history-title">
      <h3 id="history-title" className="font-sans text-[13.5px] font-semibold tracking-[-0.018em]">
        {t.detail.history}
      </h3>
      {items.length === 0 ? (
        <p className="mt-1 text-[12.5px] text-fg-subtle">{t.detail.noHistory}</p>
      ) : (
        <ol className="mt-2 space-y-1">
          {items.map((sync) => (
            <li key={sync.id} className="flex flex-wrap gap-x-3 text-[12.5px]">
              <span className="readout w-[92px] shrink-0 text-fg-subtle">
                {sync.status === 'running' ? <Spinner size={10} /> : fmt.formatAgo(sync.finishedAt ?? sync.startedAt)}
              </span>
              <span
                className={
                  sync.status === 'failed'
                    ? 'text-alert'
                    : sync.added + sync.changed + sync.removed + sync.withdrawn === 0
                      ? 'text-fg-muted'
                      : ''
                }
              >
                {syncLine(fmt, sync)}
              </span>
              {sync.triggeredBy !== 'schedule' && (
                <span className="readout text-fg-subtle">{t.detail.triggeredBy(sync.triggeredBy)}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

const Footer = ({
  folder,
  base,
  mayUse,
  onDisconnected
}: {
  folder: ConnectorFolder
  base: DrawerBase
  /** May read through the folder's account: check, resume, how often, a different account. */
  mayUse: boolean
  onDisconnected: () => void
}) => {
  const viewer = useViewer()
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  const check = useCheckNow()
  const pause = usePauseFolder()
  const resume = useResumeFolder()
  const update = useUpdateFolder()
  const start = useStartFolder()
  const disconnect = useDisconnectFolder()
  const connections = useConnections(true)
  const [confirmPause, setConfirmPause] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [withdraw, setWithdraw] = useState(false)
  const [cadence, setCadence] = useState(false)
  const [rebind, setRebind] = useState(false)
  const surface = SURFACE_LABEL[folder.surface]
  const docs = folder.stats.synced
  // Only accounts this viewer may use are listed by the server, and only those can take the folder.
  const others = (connections.data?.items ?? []).filter(
    (c) =>
      c.provider === folder.connection.provider &&
      c.id !== folder.connection.id &&
      c.health !== 'needs_reauth' &&
      mayUseConnection(c, viewer)
  )

  const interval = (ms: number) =>
    ms <= SYNC_INTERVALS.soon
      ? SYNC_INTERVALS.soon
      : ms <= SYNC_INTERVALS.fewTimesADay
        ? SYNC_INTERVALS.fewTimesADay
        : SYNC_INTERVALS.daily

  return (
    <footer className="border-t border-rule px-4 py-3">
      {folder.audience.stale && folder.status !== 'previewing' && (
        <div className="mb-3 space-y-2">
          <p className="text-[12.5px] leading-[1.45] text-fg-muted">{t.access.widened(base.name)}</p>
          <p className="text-[12.5px] font-semibold leading-[1.45]">{folderAckText(fmt, base, viewer.id)}</p>
          {mayUse && (
            <Button
              size="sm"
              variant="primary"
              disabled={start.isPending}
              onClick={() =>
                start.mutate({
                  id: folder.id,
                  ack: true,
                  ackText: folderAckText(fmt, base, viewer.id),
                  // The language it was read in, which is the half of this acknowledgement the
                  // row could not say before (PRD-27 D-7).
                  ackLocale: fmt.locale
                })
              }
            >
              {t.access.acknowledge}
            </Button>
          )}
          {start.isError && (
            <p role="alert" className="readout text-alert">
              {describeApiError(fmt.t, start.error)}
            </p>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {mayUse && folder.status === 'active' && (
          <Button
            size="sm"
            icon={<RefreshCw size={12} aria-hidden />}
            // Until someone confirms who can search what it loads (just above), a check would load
            // new files for more people than anyone agreed to; the server refuses it too.
            disabled={check.isPending || folder.syncing || folder.audience.stale}
            onClick={() => check.mutate({ id: folder.id })}
          >
            {check.isPending || folder.syncing ? t.detail.checking : t.detail.checkNow}
          </Button>
        )}
        {folder.status === 'active' &&
          (confirmPause ? (
            <span className="flex flex-wrap items-center gap-2 text-[12px] text-fg-muted">
              {t.detail.pauseConfirm(fmt, folder.displayPath.split(' / ').pop() ?? '', docs)}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => pause.mutate(folder.id, { onSettled: () => setConfirmPause(false) })}
              >
                {t.detail.pause}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmPause(false)}>
                {t.detail.keepSyncing}
              </Button>
            </span>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              icon={<PauseCircle size={12} aria-hidden />}
              onClick={() => setConfirmPause(true)}
            >
              {t.detail.pause}
            </Button>
          ))}
        {mayUse && folder.status === 'paused' && folder.connection.health !== 'needs_reauth' && (
          <Button
            size="sm"
            variant="primary"
            icon={<Play size={12} aria-hidden />}
            disabled={resume.isPending}
            onClick={() => resume.mutate(folder.id)}
          >
            {t.detail.resume}
          </Button>
        )}
        {mayUse && folder.status === 'active' && (
          <Button size="sm" variant="ghost" aria-expanded={cadence} onClick={() => setCadence(!cadence)}>
            {t.detail.howOften}
          </Button>
        )}
        {/* Judged by the account it moves to (PRD-26 D-56): `others` holds only accounts this viewer
            may use, so any maintainer can rescue a folder onto their own sign-in. */}
        {others.length > 0 && (
          <Button size="sm" variant="ghost" aria-expanded={rebind} onClick={() => setRebind(!rebind)}>
            {t.detail.rebind}
          </Button>
        )}
        {folder.status !== 'previewing' && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            aria-expanded={disconnecting}
            onClick={() => setDisconnecting(!disconnecting)}
          >
            {t.detail.disconnect}
          </Button>
        )}
      </div>

      <div role="status" className="readout mt-1.5 text-fg-subtle">
        {check.isSuccess &&
          (check.data.queued
            ? t.detail.checkQueued
            : check.data.outcome
              ? check.data.outcome.reason
                ? check.data.outcome.reason
                : t.detail.checkedResult(
                    fmt,
                    check.data.outcome.added,
                    check.data.outcome.changed,
                    check.data.outcome.removed,
                    check.data.outcome.withdrawn
                  )
              : '')}
        {(check.isError || pause.isError || resume.isError || update.isError) && (
          <span role="alert" className="text-alert">
            {describeApiError(fmt.t, check.error ?? pause.error ?? resume.error ?? update.error)}
          </span>
        )}
      </div>

      {cadence && (
        <div className="mt-3">
          <ChoiceRow
            name="cadence"
            value={String(interval(folder.intervalMs))}
            disabled={update.isPending}
            options={[
              {
                value: String(SYNC_INTERVALS.soon),
                label: t.detail.interval.soon,
                hint: t.detail.interval.soonHint(surface)
              },
              {
                value: String(SYNC_INTERVALS.fewTimesADay),
                label: t.detail.interval.fewTimes,
                hint: t.detail.interval.fewTimesHint(surface)
              },
              { value: String(SYNC_INTERVALS.daily), label: t.detail.interval.daily, hint: t.detail.interval.dailyHint }
            ]}
            onChange={(value) => update.mutate({ id: folder.id, patch: { intervalMs: Number(value) } })}
          />
        </div>
      )}

      {rebind && (
        <div className="mt-3">
          <p className="mb-1.5 text-[12.5px] text-fg-muted">{t.detail.rebindHint}</p>
          <ChoiceRow
            name="rebind"
            value={folder.connection.id}
            disabled={update.isPending}
            options={[
              {
                id: folder.connection.id,
                accountLabel: folder.connection.accountLabel,
                mode: folder.connection.mode,
                folderCount: null as number | null
              },
              ...others.map((c) => ({ ...c, folderCount: c.folderCount as number | null }))
            ].map((c) => ({
              value: c.id,
              label: c.accountLabel,
              hint:
                c.mode === 'application'
                  ? t.accounts.applicationHint
                  : c.folderCount === null
                    ? t.detail.currentAccount
                    : t.accounts.usedBy(fmt, c.folderCount)
            }))}
            onChange={(value) =>
              update.mutate({ id: folder.id, patch: { connectionId: value } }, { onSuccess: () => setRebind(false) })
            }
          />
        </div>
      )}

      {disconnecting && (
        <div className="mt-3 rounded-tile border border-rule bg-card-solid/55 px-3.5 py-3">
          <p className="text-[13px] font-semibold">
            {t.detail.disconnectTitle(folder.displayPath.split(' / ').pop() ?? folder.displayPath)}
          </p>
          <p className="mt-0.5 text-[12.5px] leading-[1.45] text-fg-muted">
            {t.detail.disconnectBody(fmt, surface, docs)}
          </p>
          <div className="mt-2.5">
            <ChoiceRow
              name="disconnect-outcome"
              value={withdraw ? 'withdraw' : 'keep'}
              options={[
                { value: 'keep', label: t.detail.keepThem, hint: t.detail.keepThemHint(base.name, surface) },
                { value: 'withdraw', label: t.detail.withdrawThem, hint: t.detail.withdrawThemHint }
              ]}
              onChange={(value) => setWithdraw(value === 'withdraw')}
            />
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant={withdraw ? 'danger' : 'primary'}
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate({ id: folder.id, withdraw }, { onSuccess: onDisconnected })}
            >
              {t.detail.confirmDisconnect}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDisconnecting(false)}>
              {t.detail.keepSyncing}
            </Button>
            {disconnect.isError && (
              <span role="alert" className="readout text-alert">
                {describeApiError(fmt.t, disconnect.error)}
              </span>
            )}
          </div>
        </div>
      )}
    </footer>
  )
}
